<template>
    <v-card :loading="loading" min-height="500" flat mx-2>
        <template slot="progress">
            <v-overlay absolute class="d-flex flex-column text-center">
                <div>
                    <v-progress-circular size="250" width="10" color="primary" indeterminate>
                        <span>In curs de incarcare...</span>
                    </v-progress-circular>
                </div>
            </v-overlay>
        </template>
        <v-container class="px-10" fluid v-if="!loading">
            <form autocomplete="off">
                <v-row>
                    <v-col cols="3">
                        <v-card elevation="2">
                            <v-card-title>Detalii achizitie</v-card-title>
                            <v-card-text>
                                <v-text-field
                                    v-model="form.document_no"
                                    :error-messages="errorMessages('document_no')"
                                    :class="{ 'error--text': hasErrors('document_no') }"
                                    class="primary--text"
                                    name="document_no"
                                    label="Numar document"
                                    data-vv-as="Numar document"
                                    data-vv-name="document_no"
                                />
                                <p class="font-weight-regular">
                                    Data document
                                </p>
                                <v-date-picker
                                    v-model="form.document_date"
                                    elevation="1"
                                    full-width
                                ></v-date-picker>
                                <v-select
                                    :items="(new $db.InternalAquisition).paymentTypes"
                                    v-model="form.payment_type"
                                    :error-messages="errorMessages('payment_type')"
                                    :class="{ 'error--text': hasErrors('payment_type') }"
                                    class="primary--text"
                                    name="payment_type"
                                    label="Metoda de plata"
                                    data-vv-as="Metoda de plata"
                                    data-vv-name="payment_type"
                                />
                                <v-autocomplete
                                    :items="(new $db.InternalAquisition).types"
                                    v-model="form.type"
                                    :error-messages="errorMessages('type')"
                                    :class="{ 'error--text': hasErrors('type') }"
                                    class="primary--text"
                                    name="type"
                                    label="Tip achizitie"
                                    data-vv-as="Tip achizitie"
                                    data-vv-name="type"
                                    :hint="aquisitionTypeHint"
                                    :persistent-hint="true"
                                />
                                <v-textarea
                                    v-model="form.remarks"
                                    :error-messages="errorMessages('remarks')"
                                    :class="{ 'error--text': hasErrors('type') }"
                                    class="primary--text"
                                    name="remarks"
                                    label="Observatii"
                                    data-vv-as="Observatii"
                                    data-vv-name="remarks"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="3">
                        <v-card elevation="2">
                            <v-card-title>Detalii furnizor</v-card-title>
                            <v-card-text>
                                <supplier-modal
                                    v-model="form.supplier_id"
                                    :field-errors="errorMessages('supplier_id')"
                                    :initial-items="[form.supplier]"
                                    @change="(supplier) => form.supplier = supplier"
                                    :expandeable="true"
                                    :show-orders-summary="_.isNil(form.id)"
                                    show-summary
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="3">
                        <v-card elevation="2">
                            <v-card-title>Detalii echipament</v-card-title>
                            <v-card-text>
                                <equipment-modal
                                    v-model="form.equipment_id"
                                    :field-errors="errorMessages('equipment_id')"
                                    :initial-items="[form.equipment]"
                                    :customer-id="Static.selfClientId"
                                    :autocomplete="true"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="3">
                        <v-card elevation="2">
                            <v-card-title>Actiuni</v-card-title>
                            <v-card-text>
                                <v-container>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-btn
                                                v-if="canEdit"
                                                :loading="form.busy"
                                                :disabled="errors.any() || form.busy"
                                                block
                                                color="success"
                                                class="my-2"
                                                @click="submit()"
                                            >
                                                <v-icon left>
                                                    save
                                                </v-icon>
                                                Salveaza
                                            </v-btn>

                                            <template v-if="!_.isNil(form.id)">
                                                <v-subheader>Fisiere</v-subheader>
                                                <v-file-input
                                                    v-model="fileUpload"
                                                    label="Click pentru adaugare"
                                                    multiple
                                                ></v-file-input>

                                                <template v-if="form.media instanceof Array && form.media.length">
                                                    <v-list-item v-for="(mediaItem, mIndex) in form.media" :key="mIndex" link @click="downloadAttachment(mediaItem)">
                                                        <v-list-item-content>
                                                            <v-list-item-title>{{ mediaItem.file_name }}</v-list-item-title>
                                                        </v-list-item-content>
                                                        <v-list-item-action>
                                                            <v-btn icon outlined color="error" @click="deleteAttachment(mediaItem)">
                                                                <v-icon>mdi-delete</v-icon>
                                                            </v-btn>
                                                        </v-list-item-action>
                                                    </v-list-item>
                                                </template>
                                            </template>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12">
                        <v-card elevation="2">
                            <v-card-title>Produse si servicii</v-card-title>
                            <v-card-text>
                                <v-simple-table dense>
                                    <template v-slot:default>
                                        <thead class="aquisition-thead">
                                        <tr>
                                            <th class="text-left" width="150">
                                                Tip
                                            </th>
                                            <th class="text-left">
                                                Denumire
                                            </th>
                                            <th class="text-left" width="200">
                                                Cod Produs
                                            </th>
                                            <th class="text-left" width="50">
                                                Cantitate
                                            </th>
                                            <th class="text-left" width="50">
                                                UM
                                            </th>
                                            <th class="text-left" width="100">
                                                Pret Unitar cu TVA
                                            </th>
                                            <th class="text-right" width="50">
                                                <v-btn :disabled="!canEdit" color="success" x-small @click="addProduct(undefined)">Adaugare</v-btn>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody class="aquisition-tbody">
                                            <v-internal-aquisition-row
                                                v-for="(product, index) in form.items"
                                                :key="`product-${index}`"
                                                :value="product"
                                                :index="index"
                                                :has-errors="hasErrors"
                                                :error-messages="errorMessages"
                                                :can-edit="canEdit"
                                                @input="(val) => form.items[index] = val"
                                                @delete="deleteProduct(index)"
                                            />
                                        </tbody>
                                        <tfoot>
                                        <tr>
                                            <td colspan="5">Total</td>
                                            <td>
                                                {{ totalValue | price({currency: 'RON'}) }}
                                            </td>
                                            <td>
                                                <v-btn :disabled="!canEdit" color="success" x-small @click="addProduct(undefined)">Adaugare</v-btn>
                                            </td>
                                        </tr>
                                        </tfoot>
                                    </template>
                                </v-simple-table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </form>
        </v-container>
    </v-card>
</template>

<script>
    import SupplierModal from 'Components/modal/SupplierModal'
    import EquipmentModal from 'Components/modal/EquipmentModal'

    import validationError from "Mixins/validation-error";
    import ACL from "Mixins/acl";
    import {Form} from "vform";
    import throttle from 'lodash/throttle'
    import { createNamespacedHelpers } from "vuex";
    const { mapGetters } = createNamespacedHelpers("auth");
    const { mapMutations } = createNamespacedHelpers("temp");

    export default {
        mixins: [validationError, ACL],
        components: {
            SupplierModal,
            EquipmentModal
        },
        props: {
            id: {
                type: [Number, String],
                required: false
            }
        },
        data: () => ({
            tab: 'general',
            loading: false,
            /* Always Declare Your Form Object */
            form: new Form({
                id: null,
                supplier_id: null,
                equipment_id: null,
                document_no: null,
                document_date: null,
                payment_type: null,
                supplier: null,
                equipment: null,
                remarks: null,
                type: null,
                items: [],
                created_by: null,
                data: {},
            }),
            aquisitionTypeHint: undefined,
            fileUpload: []
        }),
        computed: {
            ...mapGetters({
                getMe: "getMe"
            }),
            canEdit() {
                if (!this._.isNil(this.form.created_by) && this.getMe?.id !== this.form.created_by && !this.hasRole('admin')) return false
                return true
            },
            totalValue() {
                return this._.sumBy(this.form.items, (product) => { return parseFloat(product.quantity) * parseFloat(product.unit_price)})
            }
        },
        watch: {
            'form.supplier': {
                handler(val) {
                    if(val?.id) {
                        this.form.supplier_id = val.id
                    }
                },
                deep: true
            },
            'form.equipment': {
                handler(val) {
                    if(val?.id) {
                        this.form.equipment_id = val.id
                    }
                },
                deep: true
            },
            'form.type': {
                handler(val) {
                    let aquisitionTypeHint = (new this.$db.InternalAquisition).types.find(t => t.value === val)
                    if (aquisitionTypeHint) {
                        this.aquisitionTypeHint = aquisitionTypeHint.hint
                    }
                }
            },
            fileUpload: {
                handler (val) {
                    let that = this
                    if (val === undefined || (val instanceof Array && !val.length)) return
                    that.loading = true
                    let formData = new FormData();
                    for (let i = 0; i < val.length; ++i) {
                        formData.append(`attachments[${i}]`, val[i]);
                    }
                    axios.post((new this.$db.InternalAquisition({id: this.form.id})).mediaEndpoint(), formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }).then(function({data}){
                        that.form.media = []
                        for (let f = 0; f < that._.get(data, 'data', []).length; ++f) {
                            that.form.media.push(data.data[f])
                        }
                        that.fileUpload = []
                        that.showSuccess();
                    })
                    .catch(function(){
                        that.showError('A aparut o eroare necunoscuta. Va rugam contactati echipa de suport.')
                    })
                    .finally(() => {
                        that.loading = false
                    })
                }
            }
        },
        async mounted() {
            if(this.id) {
                await this.loadResource()
            }
            if(this.form.items.length < 1) {
                this.addProduct(undefined)
            }
        },
        methods: {
            loadResource: throttle(async function (val) {
                let self = this;
                this.loading = true;

                let data = await this.$db.InternalAquisition
                    .include('supplier', 'equipment', 'items', 'media')
                    .find(this.id)
                    .then(({data}) => {
                        data.items = data.items.map((product) => {
                            return {
                                id: product.id,
                                type: product.type,
                                name: product.name,
                                product_number: product.product_number,
                                quantity: product.pivot.quantity,
                                measurement: product.pivot.measurement,
                                unit_price: product.pivot.unit_price,
                            }
                        })
                        return data
                    })
                self.form = new Form(data)
                this.loading = false
            }, 500),
            async submit() {
                let self = this;
                this.$validator.validateAll().then(result => {
                    if (result) {
                        // eslint-disable-next-line
                        self.createOrUpdate();
                    } else {
                        this.showError()
                    }
                });
            },
            async createOrUpdate() {
                let self = this;
                self.form.busy = true;

                var model = new this.$db.InternalAquisition(self.form);
                if (this.id) {
                    model.id = this.id
                }

                model.save()
                    .then(response => {
                        self.$validator.reset();
                        self.form.errors.set({})
                        this.showSuccess();
                        if (this._.isNil(model.id) && response?.id) {
                            self.$nextTick(() => self.$router.push({name: "internal-aquisitions-edit", params: {id: response.id}}));
                        }
                    })
                    .catch(errors => {
                        self.form.errors.set(this.form.extractErrors(errors.response))
                        this.showError()
                    }).finally(() => {
                    self.form.busy = false;
                });
            },
            addProduct(index) {
                let definition = {
                    id: null,
                    type: null,
                    name: null,
                    product_number: null,
                    quantity: 1,
                    measurement: 'BUC',
                    unit_price: 0,
                }
                if (index) {
                    this.form.items.splice(index, 0, definition);
                } else {
                    this.form.items.push(definition);
                }
            },
            deleteProduct(index) {
                if(this.form.items.length > 1) {
                    this.form.items.splice(index, 1);
                }
            },

            async downloadAttachment(item) {
                const downloadEndpoint = (new this.$db.InternalAquisition({id: this.form.id})).mediaEndpoint()
                this.loading = true
                return window.axios.get(`${downloadEndpoint}/${item.id}`, {
                    responseType: 'blob'
                }).then((response) => {
                    const contentDisposition = this._.get(response, 'headers.content-disposition')
                    const filename = contentDisposition.split("filename=")[1];
                    const downloadUrl = window.URL.createObjectURL(new Blob([response?.data]));
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', filename); //any other extension
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }).catch((err) => {
                    console.error(err)
                }).finally(() => {
                    this.loading = false
                })
            },
            async deleteAttachment (item) {
                let that = this
                const downloadEndpoint = (new this.$db.InternalAquisition({id: this.form.id})).mediaEndpoint()
                this.loading = true
                return window.axios.delete(`${downloadEndpoint}/${item.id}`).then(({data}) => {
                    that.form.media = []
                    for (let f = 0; f < that._.get(data, 'data', []).length; ++f) {
                        that.form.media.push(data.data[f])
                    }
                }).catch((err) => {
                    console.error(err)
                }).finally(() => {
                    this.loading = false
                })
            }
        }
    };
</script>

<style lang="scss">
    .vue-gauge-item {
        text-align: center !important;
    }
    .aquisition-tbody {
        tr {
            td {
                padding: 0 5px !important;
                .v-text-field__details {
                    display: none !important;
                    &.show {
                        display: block !important;
                    }
                }
            }
        }
    }
    .aquisition-thead {
        tr {
            th {
                position: sticky;
                top: 0;
            }
        }
    }
</style>
